import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Loader2, Download, Wand2, Image as ImageIcon, Play } from 'lucide-react';
import { mockGeneratedVideos } from '../mock';
import { toast } from '../hooks/use-toast';
import Header from '../components/Header';

const AIVideo = () => {
  const [activeTab, setActiveTab] = useState('text-to-video');
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState('5s');
  const [loading, setLoading] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState(mockGeneratedVideos);
  const [uploadedImage, setUploadedImage] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Prompt required",
        description: "Please enter a description for your video",
        variant: "destructive"
      });
      return;
    }

    if (activeTab === 'image-to-video' && !uploadedImage) {
      toast({
        title: "Image required",
        description: "Please upload an image first",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const newVideo = {
        id: Date.now().toString(),
        prompt,
        thumbnailUrl: mockGeneratedVideos[Math.floor(Math.random() * mockGeneratedVideos.length)].thumbnailUrl,
        duration,
        createdAt: new Date().toISOString(),
        type: activeTab
      };
      
      setGeneratedVideos([newVideo, ...generatedVideos]);
      setLoading(false);
      toast({
        title: "Video generation started!",
        description: "Your video will be ready soon"
      });
    }, 3000);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
    <Header />

    <div className="min-h-screen bg-[#0a0a0a]">
      
      
      <div className="pt-24 px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-yellow-400/10 backdrop-blur-sm border border-yellow-400/20 rounded-full px-4 py-2 mb-6">
              <span className="text-yellow-400 text-sm font-medium">Coming Soon - Video Generation</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-4">
              AI Video Generation
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Create cinematic videos from text or animate your images with AI
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Generation Panel */}
            <div className="space-y-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
                  <TabsTrigger value="text-to-video" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                    Text to Video
                  </TabsTrigger>
                  <TabsTrigger value="image-to-video" className="data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
                    Image to Video
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="text-to-video" className="space-y-4 mt-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Describe your video
                    </label>
                    <Textarea
                      placeholder="Cinematic shot of ocean waves crashing on rocks at sunset..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Duration
                    </label>
                    <Select value={duration} onValueChange={setDuration}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        <SelectItem value="5s">5 seconds</SelectItem>
                        <SelectItem value="10s">10 seconds</SelectItem>
                        <SelectItem value="15s">15 seconds</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                <TabsContent value="image-to-video" className="space-y-4 mt-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Upload Image
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="video-image-upload"
                      />
                      <label
                        htmlFor="video-image-upload"
                        className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-yellow-400/50 transition-colors bg-white/5"
                      >
                        {uploadedImage ? (
                          <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <>
                            <ImageIcon className="w-12 h-12 text-white/40 mb-2" />
                            <span className="text-white/60 text-sm">Click to upload image</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Animation style
                    </label>
                    <Textarea
                      placeholder="Animate with smooth camera movement and cinematic effects..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[100px] bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-[#F4D03F] hover:bg-[#e5c230] text-black font-semibold py-6 rounded-lg transition-all duration-300 hover:scale-[1.02]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    Generate Video
                  </>
                )}
              </Button>

              <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4">
                <p className="text-yellow-400 text-sm">
                  <strong>Note:</strong> Video generation is currently showing mock data. Backend integration coming soon!
                </p>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-white text-lg font-semibold mb-4">Generated Videos</h3>
              <div className="grid grid-cols-2 gap-4 max-h-[600px] overflow-y-auto">
                {generatedVideos.map((video) => (
                  <Card
                    key={video.id}
                    className="group relative bg-white/5 border-white/10 overflow-hidden cursor-pointer hover:border-yellow-400/50 transition-all duration-300"
                  >
                    <img
                      src={video.thumbnailUrl}
                      alt={video.prompt}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        size="sm"
                        className="bg-[#F4D03F] hover:bg-[#e5c230] text-black mr-2"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#F4D03F] hover:bg-[#e5c230] text-black"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/80 rounded px-2 py-1 text-xs text-white">
                      {video.duration}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AIVideo;
